/**
 * Client-side Rate Limiter
 * 
 * Prevents abuse by limiting the number of requests/actions per time window
 * Stores limits in memory (resets on page reload)
 */

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  blockDurationMs?: number;
}

interface RateLimitEntry {
  count: number;
  firstAttemptTime: number;
  blockedUntil?: number;
}

class RateLimiter {
  private limits: Map<string, RateLimitEntry> = new Map();

  /**
   * Check if action is allowed
   * @param key - Unique identifier for the action (e.g., 'login', 'register')
   * @param config - Rate limit configuration
   * @returns Object with isAllowed flag and retry info
   */
  checkLimit(key: string, config: RateLimitConfig): {
    isAllowed: boolean;
    remainingAttempts: number;
    resetTime?: number;
    retryAfter?: number;
  } {
    const now = Date.now();
    const entry = this.limits.get(key);

    // Check if currently blocked
    if (entry?.blockedUntil && entry.blockedUntil > now) {
      return {
        isAllowed: false,
        remainingAttempts: 0,
        retryAfter: Math.ceil((entry.blockedUntil - now) / 1000),
      };
    }

    // If no entry or window expired, reset
    if (!entry || now - entry.firstAttemptTime > config.windowMs) {
      this.limits.set(key, {
        count: 1,
        firstAttemptTime: now,
      });
      return {
        isAllowed: true,
        remainingAttempts: config.maxAttempts - 1,
        resetTime: now + config.windowMs,
      };
    }

    // Increment counter
    entry.count++;

    // Check if limit exceeded
    if (entry.count > config.maxAttempts) {
      const blockDuration = config.blockDurationMs || config.windowMs;
      entry.blockedUntil = now + blockDuration;
      
      return {
        isAllowed: false,
        remainingAttempts: 0,
        retryAfter: Math.ceil(blockDuration / 1000),
      };
    }

    return {
      isAllowed: true,
      remainingAttempts: config.maxAttempts - entry.count,
      resetTime: entry.firstAttemptTime + config.windowMs,
    };
  }

  /**
   * Reset limits for a specific key
   */
  reset(key: string): void {
    this.limits.delete(key);
  }

  /**
   * Clear all limits
   */
  clearAll(): void {
    this.limits.clear();
  }
}

// Singleton instance
export const rateLimiter = new RateLimiter();

// Preset configurations
export const RATE_LIMITS = {
  // Authentication actions
  LOGIN: {
    maxAttempts: 5,
    windowMs: 15 * 60 * 1000, // 15 minutes
    blockDurationMs: 30 * 60 * 1000, // 30 minutes block
  },
  REGISTER: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
    blockDurationMs: 60 * 60 * 1000, // 1 hour block
  },
  RESEND_EMAIL: {
    maxAttempts: 3,
    windowMs: 5 * 60 * 1000, // 5 minutes
    blockDurationMs: 15 * 60 * 1000, // 15 minutes block
  },
  
  // Form submissions
  SUBMIT_FORM: {
    maxAttempts: 10,
    windowMs: 60 * 1000, // 1 minute
    blockDurationMs: 5 * 60 * 1000, // 5 minutes block
  },
  
  // API calls
  API_CALL: {
    maxAttempts: 60,
    windowMs: 60 * 1000, // 1 minute
    blockDurationMs: 60 * 1000, // 1 minute block
  },
} as const;

/**
 * React hook for rate limiting
 * @example
 * const { checkLimit, isBlocked, retryAfter } = useRateLimit('login', RATE_LIMITS.LOGIN);
 */
export function useRateLimit(key: string, config: RateLimitConfig) {
  const checkLimit = () => rateLimiter.checkLimit(key, config);
  const reset = () => rateLimiter.reset(key);
  
  return {
    checkLimit,
    reset,
    rateLimiter,
  };
}
